import React from 'react';
import { COMPANY_SECTION, PLACEHOLDER_CANDIDATES } from '../data/content';
import ScrollReveal from '../components/ScrollReveal';
import MagneticButton from '../components/MagneticButton';
import StudentCard from '../components/StudentCard';
import usePointerVars from '../hooks/usePointerVars';
import useMotionPrefs from '../hooks/useMotionPrefs';
import { Check } from '../components/icons';

/* ==================================================================
   CompanySection
   ------------------------------------------------------------------
   The one dark section on the page. The tonal flip is the point: it
   marks the hand-off from the student story to the hiring story, and
   gives the candidate cards somewhere to glow. An ambient light
   tracks the cursor across the panel.

   `candidates` is a prop so a real shortlist can be piped in later.
   ================================================================== */

export default function CompanySection({ candidates = PLACEHOLDER_CANDIDATES }) {
  const { reduced, pointer } = useMotionPrefs();
  const ref = usePointerVars({ disabled: reduced || !pointer });

  return (
    <section id="companies" className="hs-companies" ref={ref}>
      <span className="hs-companies__light" aria-hidden="true" />
      <span className="hs-companies__mesh" aria-hidden="true" />

      <div className="hs-shell hs-companies__grid">
        <div className="hs-companies__copy">
          <ScrollReveal as="p" className="hs-eyebrow hs-eyebrow--dark">
            {COMPANY_SECTION.eyebrow}
          </ScrollReveal>
          <ScrollReveal as="h2" className="hs-h2 hs-h2--dark hs-h2--tight" delay={80}>
            {COMPANY_SECTION.heading}
          </ScrollReveal>
          <ScrollReveal as="p" className="hs-lede hs-lede--dark" delay={150}>
            {COMPANY_SECTION.sub}
          </ScrollReveal>

          <ScrollReveal as="ul" className="hs-companies__points" delay={200}>
            {['Search by skill, track and work mode', 'Profiles with projects and resumes', 'Message shortlisted students directly'].map(
              (point, i) => (
                <li key={point} style={{ '--hs-i': i }}><Check /> {point}</li>
              )
            )}
          </ScrollReveal>

          <ScrollReveal delay={260} variant="fade">
            <MagneticButton to={COMPANY_SECTION.cta.to} variant="light" size="lg" arrow>
              {COMPANY_SECTION.cta.label}
            </MagneticButton>
          </ScrollReveal>
        </div>

        <div className="hs-companies__stack">
          {candidates.map((candidate, i) => (
            <ScrollReveal
              key={candidate.id}
              className="hs-companies__slot"
              variant="right"
              delay={i * 120}
              style={{ '--hs-i': i }}
            >
              <StudentCard student={candidate} index={i} />
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}
